import {defineMessage, FormattedMessage, useIntl} from "react-intl";
import {Checkbox, Tooltip} from "@mui/material";
import * as React from "react";
import {FormattedExtensionLimitTableHeader, NoValidSet, format_quota, useLimitTableStyles} from "./common";
import {useAuth} from "@/context/AuthProvider";
import {faToggleOff, faToggleOn} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {Limit} from "../types";
import {CapabilitiesThatCanBeUsed} from "@/constants";
import {labelForCapabilitySet} from "@/utils/translations";

type CapabilitySetsWidgetProps = {
    // All capabilities
    sets: Limit[];
    // Capabilities set by the form
    value: string[];
    // Callback to set the values
    updateValue: (value: string[]) => void;
    // Callback to check if capability can be set ('quota reached')
    setCanBeChecked: (set: Limit) => boolean;
    setCanBeUnchecked?: (set: Limit) => boolean;
    disabled: boolean;
    initialValue?: string[];
    // Whether to show table containing usage information for each capability
    showUsageInfo?: boolean;
    // show a full table with headers or hide the headers as tooltips
    compactTable?: boolean;
    // show the currently used value - not useful when creating an extension
    showUsedValues?: boolean;
    // Capabilities set by the user package
    upCaps?: string[];
    containerStyle?: React.CSSProperties;
};

// Whether to show 'toggle' icon for a capability
function CapabilityUsedDisplay({cap}: {cap: Limit}) {
    // If a capability should not be checked this way, just do not show anything
    if (!CapabilitiesThatCanBeUsed.map((c) => c.toLowerCase()).includes(cap.name)) return null;

    if (cap.user.used) return <FontAwesomeIcon icon={faToggleOn} size="lg" />;

    return <FontAwesomeIcon icon={faToggleOff} size="lg" />;
}

export const CapabilitySetsWidget = (props: CapabilitySetsWidgetProps) => {
    const {
        sets,
        disabled,
        setCanBeChecked,
        setCanBeUnchecked,
        value,
        updateValue,
        containerStyle,
        initialValue = [],
        showUsageInfo = true,
        compactTable = true,
        showUsedValues = false,
        upCaps = [],
    } = props;

    if (sets.length === 0)
        return (
            <NoValidSet
                msgDesc={defineMessage({
                    id: "no_valid_capability_set",
                    defaultMessage: "There is no valid capability set",
                })}
            />
        );

    const intl = useIntl();
    const {classes} = useLimitTableStyles();

    const auth = useAuth();
    const isSuperuser = auth.isSuperuser;

    // Main checkbox component
    const CapabilityCheckbox = ({cap}: {cap: Limit}) => {
        const upIncludes = upCaps?.includes(cap.name);
        const isChecked = value.includes(cap.name) || upIncludes;
        const canBeChecked = setCanBeChecked(cap);
        const canBeUnchecked = (setCanBeUnchecked?.(cap) ?? true) && !upIncludes;

        return (
            <Checkbox
                disabled={disabled || (isChecked && !canBeUnchecked) || (!isChecked && !canBeChecked)}
                checked={isChecked}
                style={{margin: "-7px 0"}}
                onChange={(event) => {
                    if (event.target.checked) updateValue([cap.name, ...value]);
                    else updateValue(value.filter((c) => c !== cap.name));
                }}
                color="primary"
            />
        );
    };

    return (
        <table style={containerStyle} className={classes.limitTable}>
            {!compactTable && (
                <thead>
                    <tr>
                        <FormattedExtensionLimitTableHeader header="capability" />
                        <FormattedExtensionLimitTableHeader header="allowed" />
                        {showUsageInfo && (
                            <>
                                {showUsedValues && <FormattedExtensionLimitTableHeader header="used" />}
                                <FormattedExtensionLimitTableHeader header="domain" />
                                {isSuperuser && <FormattedExtensionLimitTableHeader header="system" />}
                            </>
                        )}
                    </tr>
                </thead>
            )}
            <tbody>
                {sets.map((cap) => (
                    <tr key={cap.name}>
                        <td align="right">
                            <FormattedMessage {...labelForCapabilitySet(cap.name)} />:
                        </td>
                        <td>
                            <CapabilityCheckbox cap={cap} />
                        </td>
                        {showUsageInfo && (
                            <>
                                {showUsedValues && (
                                    <td>
                                        <CapabilityUsedDisplay cap={cap} />
                                    </td>
                                )}
                                <Tooltip
                                    title={intl.formatMessage({
                                        id: "extensionQuotaSettings.tooltips.domain",
                                        defaultMessage: "Allocated for domain / Allocated for users",
                                    })}
                                >
                                    <td>
                                        <span>
                                            {(cap.domain?.used ?? 0) +
                                                (!initialValue.includes(cap.name) && cap.form
                                                    ? 1
                                                    : initialValue.includes(cap.name) && !cap.form
                                                      ? -1
                                                      : 0)}
                                            / {cap.domain?.allocated ?? 0}
                                        </span>
                                    </td>
                                </Tooltip>
                                {isSuperuser && (
                                    <Tooltip
                                        title={intl.formatMessage({
                                            id: "extensionQuotaSettings.tooltips.system",
                                            defaultMessage: "System total allocated / System limit",
                                        })}
                                    >
                                        <td>
                                            <span>
                                                {cap.system.allocated} / {format_quota(cap.system.limit)}
                                            </span>
                                        </td>
                                    </Tooltip>
                                )}
                            </>
                        )}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};
